import { FieldError, Radio, RangeSlider, Typography } from '@/ui-kit';
import styles from './fourth-step-registartion-form.module.scss';
import { FieldBlock } from '@/components/field-block';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Profile } from '@/types/profile';
import { registrationStepFourthSchema, RegistrationStepFourthType } from '@/schemes/registration-step-fourth-schema';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce } from 'lodash';

import { GoalCard } from '@/components/goal-card';
import { useSuccessfulMessage } from '@/store/useSuccessfulMessage';
import { ButtonSubmitStep } from '../button-submit-step';
import { dataPurposes } from '@/constans/purposes';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useAuth } from '@/store/useAuth';
import { useTranslation } from 'react-i18next';

interface UpdateUserData extends RegistrationStepFourthType {
    event?: String;
}
interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: UpdateUserData) => Promise<any>;
    nextStep: () => void;
}
const DELAY_SEND_DATA = 1000;
export const FourthStepRegistartionForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    useEffect(() => {
        callYaMetrika('registration_step_3');

        eventsApi.updateUserEvent('search_param_screen');
    }, []);

    const { t } = useTranslation();

    const { setIsOpen } = useSuccessfulMessage((state) => state);

    const initialValues = useMemo(() => {
        const getMinAge = () => {
            if (!userData) return 18;
            return userData.min_age !== null ? (userData.min_age >= 60 ? 60 : userData.min_age) : 18;
        };

        const getMaxAge = () => {
            if (!userData) return 24;
            return userData.max_age !== null ? (userData.max_age >= 60 ? 60 : userData.max_age) : 24;
        };
        return {
            min_age: getMinAge(),
            max_age: getMaxAge(),
            purpose: userData?.purpose ?? 'not_specified',
            looks_for: userData?.looks_for || 'any',
        };
    }, [userData]);

    const [isLoading, setIsLoading] = useState(false);
    const [isGoalSelected, setIsGoalSelected] = useState<Profile['purpose']>(initialValues.purpose);

    const methods = useForm<RegistrationStepFourthType>({
        resolver: zodResolver(registrationStepFourthSchema),
        defaultValues: initialValues,
    });
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = methods;

    const completeRegistration = useAuth(state => state.completeRegistration)

    const onSubmit = async (data: RegistrationStepFourthType) => {
        console.log(data);

        try {
            setIsLoading(true);

            await onUpdateUserData({ event: 'registration_complete', ...data });

            await completeRegistration()

            await new Promise<void>((resolve) => {
                setTimeout(() => {
                    nextStep();
                    setIsOpen(true);
                    resolve();
                }, 500);
            });
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };
    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
            callYaMetrika('who_should_show_you');
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const handleChangeRangeAge = (value: any) => {
        methods.setValue('min_age', value[0]);
        methods.setValue('max_age', value[1]);
        eventsApi.updateUserEvent('registration_min_max_age');
        handleChangeUserDataDebounce({ min_age: value[0], max_age: value[1] });
    };
    const handleChangeGoal = (value: any) => {
        setIsGoalSelected(value);
        methods.setValue('purpose', value);
        eventsApi.updateUserEvent('registration_purpose');
        handleChangeUserDataDebounce({ purpose: value });
    };
    const handleChangeLooksFor = (gender: string) => {
        eventsApi.updateUserEvent('registration_looks_for');
        handleChangeUserDataDebounce({ looks_for: gender });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('who_are_you_looking_for')}
                    </Typography>
                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography variant="label" fontWeight="semibold">
                                {t('who_are_you_looking_for')}
                            </Typography>
                        </div>
                        <div className={styles.formRadioGroup}>
                            <Radio
                                label={t('male_secondary')}
                                value="male"
                                {...register('looks_for', {
                                    onChange(e) {
                                        handleChangeLooksFor(e.target.value);
                                    },
                                })}
                            />
                            <Radio
                                label={t('female_secondary')}
                                value="female"
                                {...register('looks_for', {
                                    onChange(e) {
                                        handleChangeLooksFor(e.target.value);
                                    },
                                })} />
                            <Radio
                                label={t('optional')}
                                value="any"
                                {...register('looks_for', {
                                    onChange(e) {
                                        handleChangeLooksFor(e.target.value);
                                    },
                                })} />
                        </div>
                        <FieldError message={errors?.looks_for?.message} />
                    </FieldBlock>
                    <div className={styles.formCardsBlock}>
                        {dataPurposes.map((purpose) => {
                            const IconComponent = purpose.illustarion;
                            return (
                                <GoalCard
                                    key={purpose.value}
                                    text={t(purpose.text)}
                                    icon={<IconComponent />}
                                    active={purpose.value === isGoalSelected}
                                    onChange={() => handleChangeGoal(purpose.value)} />
                            );
                        })}
                    </div>
                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography variant="label" fontWeight="semibold">
                                {t('age_range')}
                            </Typography>
                        </div>
                        <RangeSlider
                            maxStart={initialValues?.max_age}
                            minStart={initialValues?.min_age}
                            onChange={handleChangeRangeAge} />
                        <FieldError message={errors?.max_age?.message || errors?.min_age?.message} />
                    </FieldBlock>
                </div>
                <ButtonSubmitStep
                    title={t('registration.finish_signup')}
                    isLoading={isLoading}
                    stepNumber={3}
                    isGray />
            </form>
        </FormProvider>
    );
};
